import { useEffect, useRef, useState } from 'react';
import { useFeatureFlag } from '@harnessio/ff-react-client-sdk';
import { useTranslation } from 'react-i18next';
import {
  Toast,
  TOAST_POSITIONS,
  BUTTON_VARIANTS,
  BUTTON_ICON_COLORS,
  IconCancelOutlined,
} from 'cdk-radial';
import { useAnalytics, whenStartupTracked } from '../third-party-integration/amplitude';
import {
  useAdoptEaseConfig,
  useAdoptEaseUsageByUser,
  checkAdoptEaseApplicationStatus,
  checkAdoptEaseDriveStatus,
} from './useAdoptEase';
import { useApplicationStore } from '../home/ApplicationStore';
import { Application } from '../home/ApplicationInterface';
import { Workflow } from '../home/WorkflowInterface';
import { useAdoptEaseStore } from './useAdoptEaseStore';
import getUserIdFromOkta from '../utils/getUserIdFromOkta';
import { AMPLITUDE_EVENTS } from '../../constants';
import { IAdoptEaseWorkflow } from '../../types';
import moment from 'moment';

// Constants
const OPENED_APPS_KEY = 'opened-apps';
const ADOPT_EASE_TOAST_SHOWN_KEY = 'adoptEaseToastShown';
const TOAST_VISIBILITY_DURATION_MS = 5000;
const TOAST_Z_INDEX = 999;
const TOAST_MARGIN_TOP = '70px';
const TOAST_WIDTH = '750px';

// Types
interface AdoptEaseApp {
  appId: string;
  [key: string]: any;
}

interface IAdoptEaseConfig {
  [key: string]: any;
}

interface UseAdoptEaseConfigResult {
  isSuccess: boolean;
  data: {
    data: IAdoptEaseConfig[];
  };
}

const verifyAndEnableAdoptEaseApplications = (
  adoptEaseConfig: any | null,
  applications: Application[],
  userId: string
): void => {
    const applicationNames = applications?.map(a => a?.name) ?? [];
    const exists = adoptEaseConfig?.some(
      (c:any) => c?.application && applicationNames.includes(c.application)
    );
    if (exists) {
      checkAdoptEaseApplicationStatus(userId);
    }
};

const verifyAndEnableAdoptEaseDrive = (
  adoptEaseConfig: any | null,
  workflows: Workflow[],
  userId: string
): void => {
    const workflowNames = workflows?.map(a => a?.name) ?? [];
    const exists =  adoptEaseConfig?.some(
      (c:any) => c?.workflow && workflowNames.includes(c.workflow)
    );
    if (exists) {
      checkAdoptEaseDriveStatus(userId);
    }
};

const AdoptEaseContainer = (): JSX.Element => {
  const { trackEvent } = useAnalytics();
  const toastScheduledRef = useRef(false);
  const { t } = useTranslation();

  // Feature flags
  const adoptEaseApplicationFlag = useFeatureFlag('UNIFY_UI_ADOPTEASE_APPLICATION');
  const adoptEaseDriveFlag = useFeatureFlag('UNIFY_UI_ADOPTEASE');
  const adoptEaseJournalEntryFlag = useFeatureFlag('UNIFY_UI_ADOPTEASE_JOURNALE');
  const adoptEaseCustomerFlag = useFeatureFlag('UNIFY_UI_ADOPTEASE_CUSTOMER');
  const adoptEaseVendorFlag = useFeatureFlag('UNIFY_UI_ADOPTEASE_VENDOR');

  const userConfig = useAdoptEaseUsageByUser();
  const defaultConfig = useAdoptEaseConfig() as UseAdoptEaseConfigResult;

  const { applications, workflows, isDriveAppUser } = useApplicationStore() as any;

  const adoptEaseUsageData = useAdoptEaseStore((state: any) => state.adoptEaseUsageData);
  const adoptEaseDefaultConfigData = useAdoptEaseStore((state: any) => state.adoptEaseDefaultConfigData) as AdoptEaseApp[];
  const adoptEaseApps = useAdoptEaseStore((state: any) => state.adoptEaseApps) as AdoptEaseApp[];
  const adoptEaseWorkflows = useAdoptEaseStore((state: any) => state.adoptEaseWorkflows) as any[];

  const setAdoptEaseDefaultConfigData = useAdoptEaseStore((state: any) => state.setAdoptEaseDefaultConfigData);
  const setAdoptEaseUsageData = useAdoptEaseStore((state: any) => state.setAdoptEaseUsageData);
  const setFilteredAdoptEaseWorkflows = useAdoptEaseStore((state: any) => state.setFilteredAdoptEaseWorkflows);
  const setNeverUsedApps = useAdoptEaseStore((state: any) => state.setNeverUsedApps);

  // Local state
  const [showAdoptEaseToast, setShowAdoptEaseToast] = useState<boolean>(false);

  useEffect(() => {
    if (userConfig?.isSuccess) {
        setAdoptEaseUsageData(userConfig.data ?? []);
    }
  }, [setAdoptEaseUsageData,userConfig?.isSuccess,userConfig?.data]);

  useEffect(() => {
    if (defaultConfig?.isSuccess) {
        setAdoptEaseDefaultConfigData(defaultConfig.data ?? []);
    }
  }, [setAdoptEaseDefaultConfigData, defaultConfig?.isSuccess, defaultConfig?.data]);

  useEffect(() => {
    if (adoptEaseApplicationFlag && adoptEaseDefaultConfigData && applications) {
      verifyAndEnableAdoptEaseApplications(
        adoptEaseDefaultConfigData,
        applications,
        getUserIdFromOkta()
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adoptEaseApplicationFlag, adoptEaseDefaultConfigData, applications]);

  useEffect(() => {
     if (isDriveAppUser 
        &&  (adoptEaseDriveFlag || adoptEaseJournalEntryFlag || adoptEaseCustomerFlag || adoptEaseVendorFlag) 
        && adoptEaseDefaultConfigData && workflows){
        verifyAndEnableAdoptEaseDrive(
          adoptEaseDefaultConfigData, 
          workflows, 
          getUserIdFromOkta());
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ isDriveAppUser, adoptEaseDriveFlag, adoptEaseJournalEntryFlag, adoptEaseCustomerFlag, adoptEaseVendorFlag, adoptEaseDefaultConfigData, workflows]);

  // Handler to reset toast visibility
  const resetToastState = (): void => {
    setShowAdoptEaseToast(false);
  };

  // Helper function to process adopt ease apps and determine which to show
  const processAdoptEaseApps = (): void => {

    if (!adoptEaseApps?.length) return;

    const openedApps = new Set(
      JSON.parse(localStorage.getItem(OPENED_APPS_KEY) || '[]')
    );
    const neverUsedApps = adoptEaseApps.filter(
      (app: AdoptEaseApp) => !openedApps.has(app.appId)
    );

    const applicationIds = new Set(applications?.map((a: any) => a.id) ?? []);
    const filteredNeverUsedApps = neverUsedApps.filter((app: AdoptEaseApp) =>
      applicationIds.has(app.appId)
    );

    setNeverUsedApps(filteredNeverUsedApps);

    const hasShownToast = localStorage.getItem(ADOPT_EASE_TOAST_SHOWN_KEY);
    if (filteredNeverUsedApps.length > 0 && !hasShownToast && !toastScheduledRef.current) {
      // Wait for the startup events so "AdoptEase Toast Seen" follows Guides & Surveys boot.
      toastScheduledRef.current = true;
      whenStartupTracked().then(() => {
        trackEvent(AMPLITUDE_EVENTS.ADOPTEASE_TOAST_SEEN);
        setShowAdoptEaseToast(true);
        localStorage.setItem(ADOPT_EASE_TOAST_SHOWN_KEY, 'true');
      });
    }
  };


  const processAdoptEaseWorkflows = () => {
    if (adoptEaseWorkflows) {
        const workflowMap = workflows?.reduce((acc:any, w:any) => {
            if (w?.name) acc.set(w.name, w);
            return acc;
        }, new Map());

        const updated = adoptEaseWorkflows
            .filter((w:any) => {
                if (!w?.workflow) return false;
                if (!workflowMap.has(w.workflow)) return false;

                if (w.workflow === "Journal Entry") {
                    return adoptEaseJournalEntryFlag === true;
                }
                if (w.workflow === "Customer") {
                    return adoptEaseCustomerFlag === true;
                }
                if (w.workflow === "Vendor") {
                    return adoptEaseVendorFlag === true;
                }
                return adoptEaseDriveFlag === true;
            })
            .map((w:any) => {
                const matched = workflowMap.get(w.workflow);
                return {
                    ...w,
                    cdkuUrl: matched?.cdkuUrl ?? (w as any).cdkuUrl
                };
            });

        if (updated && updated.length > 0) {
            const userUsageMap = new Map(adoptEaseUsageData?.usage?.map((item:any) => 
              [item.workflow, moment(item.remindedOn)]));
            const activeConfigs = adoptEaseDefaultConfigData.filter(c => c.workflow && c.isActive);
            const configMap = new Map(activeConfigs.map(c => [c.workflow as string, c]));

            const filtered: IAdoptEaseWorkflow[] = [];
            updated.forEach(w => {
                const cfg = configMap.get(w.workflow);
                if (!cfg) return;

                const reminderDuration = parseInt(cfg.reminderDuration, 10);
                const remindedOn = userUsageMap.has(w.workflow)
                    ? moment.utc(userUsageMap.get(w.workflow) as any).local().startOf('day')
                    : null;
                const reminderThreshold = moment().subtract(reminderDuration, 'days').startOf('day');

                if (!remindedOn || remindedOn.isBefore(reminderThreshold)) {
                    filtered.push(w);
                }
            });
          setFilteredAdoptEaseWorkflows(filtered);
        }

    }
  }

  // Process adopt ease apps when they change
  useEffect(() => {
    processAdoptEaseApps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adoptEaseApps,adoptEaseDefaultConfigData, applications]);

  // Process adopt ease workflows when they change
  useEffect(() => {
    processAdoptEaseWorkflows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adoptEaseWorkflows,adoptEaseUsageData, adoptEaseDefaultConfigData]);

  return (
    <>
      {showAdoptEaseToast && (
        <Toast
          id="outage-notification-toast"
          datatestid="outage-notification-toast"
          position={TOAST_POSITIONS.FIXED}
          style={{
            marginTop: TOAST_MARGIN_TOP,
            zIndex: TOAST_Z_INDEX,
            width: TOAST_WIDTH,
          }}
          variant="positive"
          visibilityDuration={TOAST_VISIBILITY_DURATION_MS}
          onVisibilityDurationEnd={resetToastState}
          actions={[
            {
              onClick: resetToastState,
              variant: BUTTON_VARIANTS.ICON,
              icon: <IconCancelOutlined />,
              iconColor: BUTTON_ICON_COLORS.GRAY,
              text: '',
            },
          ]}
          content={
            <div style={{ alignItems: 'center', gap: '6px', fontSize: '15px' }}>
              <span>{t('home.adopteaseAppFirst')}</span>
              <span style={{ fontWeight: 'bold' }}>{`"${t('home.adopteaseTryNow')}"`}</span>
              <span>{t('home.adopteaseAppSecond')}</span>
            </div>
          }
        />
      )}
    </>
  );
};

export default AdoptEaseContainer;